<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { findBySlug, findNeighbours } from "@/blog/use-posts";
import { useBlogPostSeoHead } from "@/composables/use-blog-seo-head";
import { BLOG_INDEX_PATH, blogPostPath } from "@/seo/routes-manifest";

const route = useRoute();
const h1Ref = ref<HTMLHeadingElement | null>(null);

const slug = computed(() => String(route.params.slug ?? ""));
const post = computed(() => findBySlug(slug.value));
const neighbours = computed(() => findNeighbours(slug.value));

async function focusH1() {
  await nextTick();
  h1Ref.value?.focus();
}

onMounted(focusH1);
watch(slug, focusH1);

useBlogPostSeoHead(() => post.value);
</script>

<template>
  <main class="blog-post">
    <div class="blog-post__inner">
      <template v-if="post">
        <article :data-drop-cap="post.dropCap">
          <router-link :to="BLOG_INDEX_PATH" class="blog-post__back">
            <UIcon name="i-lucide-arrow-left" class="size-4" />
            Back to all essays
          </router-link>

          <h1 ref="h1Ref" tabindex="-1" class="blog-post__title">
            {{ post.title }}
          </h1>

          <p class="blog-post__meta">
            <time :datetime="post.publishDate">{{ post.publishDateDisplay }}</time>
            · {{ post.author }} · {{ post.readTimeMinutes }} min read
          </p>

          <img
            v-if="post.heroImageUrl"
            :src="post.heroImageUrl"
            :alt="post.title"
            class="blog-post__hero"
            loading="eager"
          />

          <div class="blog-post__body" v-html="post.body" />

          <aside class="blog-post__cta">
            <p class="blog-post__cta-text">
              Cresci turns essays like this into rhythms your family actually keeps.
            </p>
            <router-link to="/features" class="blog-post__cta-link">
              See how Cresci works
              <UIcon name="i-lucide-arrow-right" class="size-4" />
            </router-link>
          </aside>

          <nav
            v-if="neighbours.prev || neighbours.next"
            aria-label="Essay navigation"
            class="blog-post__nav"
          >
            <router-link
              v-if="neighbours.prev"
              :to="blogPostPath(neighbours.prev.slug)"
              class="blog-post__nav-prev"
            >
              <span class="blog-post__nav-label">Previous</span>
              <span class="blog-post__nav-title">
                <UIcon name="i-lucide-arrow-left" class="mr-1 inline size-4" />
                {{ neighbours.prev.title }}
              </span>
            </router-link>
            <span v-else />

            <router-link
              v-if="neighbours.next"
              :to="blogPostPath(neighbours.next.slug)"
              class="blog-post__nav-next"
            >
              <span class="blog-post__nav-label">Next</span>
              <span class="blog-post__nav-title">
                {{ neighbours.next.title }}
                <UIcon name="i-lucide-arrow-right" class="ml-1 inline size-4" />
              </span>
            </router-link>
            <span v-else />
          </nav>
        </article>
      </template>

      <section v-else class="blog-post__missing">
        <h1 ref="h1Ref" tabindex="-1" class="blog-post__missing-title">
          We can't find that essay.
        </h1>
        <p class="blog-post__missing-body">
          It may have moved or been retitled. Browse all essays below.
        </p>
        <router-link :to="BLOG_INDEX_PATH" class="blog-post__missing-back blog-post__back">
          <UIcon name="i-lucide-arrow-left" class="size-4" />
          All essays
        </router-link>
      </section>
    </div>
  </main>
</template>
